import type { FxQuote, Money } from '@urbanrats/contracts';
import { atomicOf } from './money.ts';
import { fromUtcInstant } from './time.ts';

export type ApproxResult =
  | { kind: 'OK'; amountAtomic: bigint; scale: number; currency: string; quote: FxQuote }
  | { kind: 'NO_QUOTE' }
  | { kind: 'STALE'; quote: FxQuote };

/**
 * Display-only approximate conversion (shown with "~" and the quote's provider/rateId/asOf).
 * Settlement always stays in the wallet currency; an expired quote yields STALE, never a number.
 * Floor in the target's display scale: amount × num × 10^target / (den × 10^scale).
 */
export function approxConvert(amount: Money, target: { currency: string; scale: number }, quotes: readonly FxQuote[], nowMs: number): ApproxResult {
  const q = quotes.find((x) => x.base === amount.currency && x.quote === target.currency);
  if (!q) return { kind: 'NO_QUOTE' };
  if (nowMs > fromUtcInstant(q.asOf) + q.ttlMs || nowMs < fromUtcInstant(q.asOf)) return { kind: 'STALE', quote: q };
  const num = atomicOf(amount) * BigInt(q.rate.numerator) * 10n ** BigInt(target.scale);
  const den = BigInt(q.rate.denominator) * 10n ** BigInt(amount.scale);
  return { kind: 'OK', amountAtomic: num / den, scale: target.scale, currency: target.currency, quote: q };
}
